<template>
    <div>
        <breadcrumbs :text="personLabel" />
        <div v-if="client.prefix || client.firstName || client.lastName" class="text-h5 font-weight-bold py-2">
            {{ `${client.prefix || ''} ${client.firstName || ''} ${client.lastName || ''}` }}
        </div>
        <v-tabs v-model="selectedTabIndex" class="mb-4">
            <v-tab v-for="tab in tabs" :key="tab.name" :data-cy="`tab-${tab.name}`">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-window v-model="selectedTabIndex" class="overflow--visible">
            <v-window-item v-for="tab in tabs" :key="tab.name">
                <client-form v-if="tab.name === 'info'" :person-data="client" />
                <documents-form v-if="tab.name === 'passport'" :person="client" type="crmperson" />
                <orders-page
                    v-if="tab.name === 'reservations'"
                    ref="orders"
                    company-page-mode
                    @new-order-request-close="onNewOrderRequestClose"
                >
                    <template v-slot:breadcrumbs><div /> </template>
                    <template v-slot:agentsPaymentsUpload><div /> </template>
                    <template v-slot:bookButtons>
                        <div class="d-flex align-center pb-4">
                            <v-btn data-cy="search-and-book" class="bg-primary" size="small" @click="searchAndBook">
                                {{ $t('new_order') }}
                            </v-btn>
                            <v-btn
                                v-if="$conf.public.account.newReservationRequest"
                                size="small"
                                class="bg-grey-lighten-4 ms-2"
                                data-cy="new-order-request-for-client"
                                @click="newOrderRequestForClient"
                            >
                                {{ $t('new_reservation_request') }}
                            </v-btn>
                        </div>
                    </template>
                </orders-page>
                <communications
                    v-if="tab.name === 'communications'"
                    :communications="communications"
                    :communications-loading="communicationsLoading"
                    :communications-total-count="communicationsCount"
                    :communication-data-for-create="{
                        accountId: client.personId,
                        accountType: 'PRIVATE_CLIENT',
                    }"
                    @get-communications="getCommunications"
                >
                    <template v-slot="{getCommunications}">
                        <communications-filters
                            v-model="communicationFilters"
                            :has-active-filters="hasActiveCommunicationFilters"
                            company-page-mode
                            @submit="getCommunications"
                            @reset="resetCommunicationFilters(getCommunications)"
                        />
                    </template>
                </communications>
                <eco-elite v-if="tab.name === 'eco_elite'" :client-id="client.personId" :client="client" />
                <settings-form v-if="tab.name === 'settings'" :client-id="client.personId" />
                <loyalty-points v-if="tab.name === 'loyalty'" :client-id="client.personId" />
            </v-window-item>
        </v-window>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import CompanyProfile from '~src/components/account/companyProfile.src'
    import Breadcrumbs from '~src/components/breadcrumbs.src'
    import ClientForm from '~src/components/account/forms/clientForm.src'
    import DocumentsForm from '@/components/account/clients/DocumentsForm.vue'
    import OrdersPage from '~src/pages/account/ordersPage.src'
    import {orderFilters} from '@/utils/store-blank-states'
    import {useRecentViewsData} from '~/composables/recent-views'
    import CommunicationsFilters from '~/components/account/communications/CommunicationsFilters.vue'
    import Communications from '~/components/account/communications/Communications.vue'
    import {clean, clone, isEqual} from '~/utils/helpers'
    import SettingsForm from '~/components/account/clients/SettingsForm.vue'
    import LoyaltyPoints from '~/components/account/clients/LoyaltyPoints.vue'
    import EcoElite from '~/components/account/EcoElite.vue'
    import {blankCommunicationFilters} from '@/utils/communications-blank-states'

    const initData = () => ({
        prefix: null,
        firstName: null,
        lastName: null,
        middleName: null,
        isLatinTheSame: false,
        foreignFirstName: null,
        foreignLastName: null,
        personId: null,
        referralLevel: null,
        registrationDate: null,
        reservationsNumber: 0,
        totalLoyaltyPoints: 0,
        active: true,
        contactPhone: null,
        secondContactPhone: null,
        thirdContactPhone: null,
        email: null,
        secondEmail: null,
        thirdEmail: null,
        fax: null,
        postalCode: null,
        addressLine: null,
        countryId: null,
        cityId: null,
        citizenshipId: null,
        birthdate: null,
        birthplace: null,
        travelPreferences: null,
        extraInfos: [],
        allergy: null,
        disability: null,
        company: null,
        position: null,
        emergencyContactPrefix: null,
        emergencyContactFirstName: null,
        emergencyContactLastName: null,
        emergencyContactPhone: null,
        emergencyContactEmail: null,
        comments: null,
        b2cId: null,
        managerId: null,
        language: null,
        stage: null,
        priority: null,
        memberLevelId: null,
        expirationDate: null,
        externalMemberID: null,
        avatar: null,
    })

    @Component({
        components: {
            OrdersPage,
            ClientForm,
            CompanyProfile,
            Breadcrumbs,
            DocumentsForm,
            Communications,
            CommunicationsFilters,
            SettingsForm,
            LoyaltyPoints,
            EcoElite,
        },
        async setup() {
            const app = useNuxtApp()
            const $route = useRoute()
            const params = $route.params
            const $api = app.$api
            const {data} = await useAsyncData(`company-client-${params.id}`, async () => {
                if (!params.id || params.id === '0') return initData()
                try {
                    const client = (await $api.privateClients.get({id: params.id})).persons[0]
                    client.active = client.active === 'true'
                    if (client?.extraInfos) {
                        client?.extraInfos.map(({type, info}) => {
                            if (type === 'DISABILITY') client.disability = info
                            if (type === 'ALLERGY') client.allergy = info
                        })
                    }
                    if (client) {
                        const linkName = `${client.firstName} ${client.lastName}`
                        await useRecentViewsData($route, linkName)
                    }
                    return client
                } catch (e) {
                    return initData()
                }
            })
            return {client: data.value}
        },
    })
    export class CompanyClientPage extends Vue {
        client = initData()
        selectedTabIndex: number = 0
        tempFilters = null
        communicationFilters = blankCommunicationFilters()
        communicationsLoading: boolean = false
        communications = []
        communicationsCount: number = 0

        get tabs() {
            let tabs = [{name: 'info', label: this.$t('information')}]

            if (+this.$route.params.id !== 0) {
                tabs = [
                    ...tabs,
                    {name: 'passport', label: this.$t('passport')},
                    {name: 'reservations', label: this.$t('orders')},
                ]

                if (!this.isAgency && !this.isTO2) {
                    tabs = [
                        ...tabs,
                        {name: 'communications', label: this.$t('communications')},
                        ...(this.$conf.public.reservationFilters.loyaltyPoints
                            ? [{name: 'loyalty', label: this.$t('loyalty')}]
                            : []),
                    ]
                }
                tabs = [...tabs, {name: 'settings', label: this.$t('settings')}]

                // Add Eco Elite tab as the last tab for all user types
                tabs = [...tabs, {name: 'eco_elite', label: this.$t('eco_elite')}]
            }

            return tabs.filter(({name}) => name !== 'communications')
        }

        mounted() {
            this.tempFilters = persistentStore.orderFilters
            persistentStore.CLEAR_ORDER_FILTERS()
            persistentStore.SET_ORDER_FILTERS(
                Object.assign({}, persistentStore.orderFilters, {client: {...this.client}})
            )

            // Set the current client page ID in the runtime store
            // This is used to filter orders by clientId when viewing a specific client's reservations
            // to prevent showing orders from other clients with the same name
            runtimeStore.SET_CURRENT_CLIENT_PAGE_ID(this.client.personId || null)
        }

        beforeUnmount() {
            persistentStore.SET_ORDER_FILTERS(Object.assign({}, this.tempFilters, {client: orderFilters()['client']}))

            // Clear the current client page ID to prevent filtering on other pages
            runtimeStore.SET_CURRENT_CLIENT_PAGE_ID(null)
        }

        onNewOrderRequestClose() {
            persistentStore.SET_SELECTED_COMPANY_AND_AGENT(null)
            persistentStore.SET_SELECTED_PRIVATE_CLIENT(null)
        }

        searchAndBook() {
            persistentStore.SET_SELECTED_PRIVATE_CLIENT({...this.client})
            this.$router.push({name: 'home'})
        }

        newOrderRequestForClient() {
            persistentStore.SET_SELECTED_PRIVATE_CLIENT({...this.client})
            if (this.$refs.orders?.[0]) {
                this.$refs.orders[0].newOrderRequestModal = true
            }
        }

        get personLabel() {
            if (this.client.personId) {
                const {prefix, firstName, lastName} = this.client
                return `${prefix || ''} ${firstName || ''} ${lastName || ''}`
            }
            return this.$t('new_client_profile')
        }

        get hasActiveCommunicationFilters() {
            return !isEqual(blankCommunicationFilters(), this.communicationFilters)
        }

        async getCommunications({itemsPerPage, page}): Promise<void> {
            try {
                this.communicationsLoading = true
                const filters = clone(this.communicationFilters)
                clean(filters)
                if (filters.communicationDateFrom)
                    filters.communicationDateFrom = this.$dateFns.format(filters.communicationDateFrom) + 'T00:00'
                if (filters.createdDateFrom)
                    filters.createdDateFrom = this.$dateFns.format(filters.createdDateFrom) + 'T00:00'
                if (filters.modifiedDateFrom)
                    filters.modifiedDateFrom = this.$dateFns.format(filters.modifiedDateFrom) + 'T00:00'
                if (filters.communicationDateTo)
                    filters.communicationDateTo = this.$dateFns.format(filters.communicationDateTo) + 'T23:59'
                if (filters.createdDateTo)
                    filters.createdDateTo = this.$dateFns.format(filters.createdDateTo) + 'T23:59'
                if (filters.modifiedDateTo)
                    filters.modifiedDateTo = this.$dateFns.format(filters.modifiedDateTo) + 'T23:59'
                const {communications, count} = await this.$api.communications.get({
                    ...filters,
                    ...{accountId: this.client.personId},
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                    typeNEQ: 'NOTES',
                })
                this.communications = communications
                this.communicationsCount = count
                this.communicationsLoading = false
            } catch (e) {
                this.communicationsCount = 0
                this.communications = []
            } finally {
                this.communicationsLoading = false
            }
        }

        resetCommunicationFilters(getCommunications): void {
            this.communicationFilters = blankCommunicationFilters()
            getCommunications()
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }
    }

    export default CompanyClientPage
</script>
